import { Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { retryWhen, catchError, switchMap, map, mergeMap, take } from 'rxjs/operators';
import { CryptoService } from '../services/crypto.service';
import { SECURITY_CONFIG, SecurityConfig, PUBLIC_KEY_HEADER_NAME } from '../models/security-config.interface';

/** APP_INITIALIZER: Fetches public key, verifies hash, blocks app until ready */
export function initializeSecurityFactory(
  http: HttpClient,
  cryptoService: CryptoService,
  config: SecurityConfig
): () => Observable<void> {

  return () => {
    if (config.enableEncryption === false) {
      return of(void 0);
    }

    if (!config.publicKeyEndpoint) {
      return throwError(() => new Error('publicKeyEndpoint required'));
    }

    // Check for cached public key in sessionStorage first
    return new Observable<void>(subscriber => {
      const cachedPemKey = cryptoService.getCachedPemKey();
      
      if (cachedPemKey) {
        // Found cached PEM key - validate and use it (same flow as API response)
        validateAndSetPublicKey(cryptoService, config, cachedPemKey).then(() => {
          subscriber.next();
          subscriber.complete();
        }).catch(err => {
          // Cache validation failed, clear and fetch from API
          cryptoService.clearCachedPublicKey();
          
          fetchPublicKeyFromAPI(http, cryptoService, config).subscribe({
            next: () => {
              subscriber.next();
              subscriber.complete();
            },
            error: (err) => subscriber.error(err)
          });
        });
      } else {
        // No cached key, fetch from API
        fetchPublicKeyFromAPI(http, cryptoService, config).subscribe({
          next: () => {
            subscriber.next();
            subscriber.complete();
          },
          error: (err) => subscriber.error(err)
        });
      }
    });
  };
}

/** Validate PEM key hash and import it (reusable for both cached and API keys) */
async function validateAndSetPublicKey(
  cryptoService: CryptoService,
  config: SecurityConfig,
  pemKey: string
): Promise<void> {
  // Verify hash
  const isValid = await cryptoService.verifyPublicKeyHash(pemKey);
  if (!isValid) {
    throw new Error('Public key hash mismatch - possible MITM attack');
  }
  
  // Import and set
  const cryptoKey = await cryptoService.importPublicKey(pemKey);
  cryptoService.setPublicKey(cryptoKey, pemKey);
  
}

/** Helper function to fetch public key from API */
function fetchPublicKeyFromAPI(http: HttpClient, cryptoService: CryptoService, config: SecurityConfig): Observable<void> {
  const retries = config.publicKeyFetchRetries || 3;
  
  return http.get<void>(config.publicKeyEndpoint!, { observe: 'response' }).pipe(
    retryWhen(errors => 
      errors.pipe(
        mergeMap((error, index) => {
          const retryCount = index + 1;
          if (retryCount > retries) {
            return throwError(error);
          }
          const delayMs = Math.pow(2, retryCount - 1) * 1000;
          return timer(delayMs);
        }),
        take(retries)
      )
    ),

    switchMap((response) => {
      const pemKey = response.headers.get(PUBLIC_KEY_HEADER_NAME) || 
                     response.headers.get(PUBLIC_KEY_HEADER_NAME.toLowerCase());
      if (!pemKey) {
        throw new Error(`${PUBLIC_KEY_HEADER_NAME} header not found. Set Access-Control-Expose-Headers`);
      }
      
      // Use the shared validation function
      return validateAndSetPublicKey(cryptoService, config, pemKey);
    }),

    map(() => {
      return void 0;
    }),

    catchError((error) => {
      return throwError(() => error);
    })
  );
}
